import { ref } from 'vue';
import { useSessionStore } from '@apparatix/stores/useSessionStore';
import { useBuildStore } from '@apparatix/stores/useBuildStore';
import {
  contractHeader,
  CONTRACT_REJECTED_STATUS,
  CONTRACT_REJECTION_HEADER,
} from '@apparatix/constants/ClientContract';

/**
 * A response whose status code the caller needs to branch on, for the handful of
 * endpoints where the status *is* the meaning — login being the obvious one,
 * where 422 and 429 say different things and both are expected. Never throws.
 */
export interface FetchResult<T> {
  status: number;
  ok: boolean;
  data: T | null;
}

export const useFetch = () => {
  const controller = ref<AbortController>();

  const getToken = () => {
    const str = `; ${document.cookie}`;
    const parts = str.split('; XSRF-TOKEN=');
    if (parts.length !== 2) return null;

    const encoded = parts.pop().split(';').shift();
    return decodeURIComponent(encoded);
  };

  /**
   * Flag a lost session so the shell can offer an in-place re-login. This only
   * marks the session suspect — a probe decides whether it is really gone — so
   * it is safe to call from every response path.
   */
  const noteAuthFailure = (response: Response) => {
    if (response.status === 401 || response.status === 419) {
      useSessionStore().markSuspect(response.status === 419 ? '419' : '401');
    }

    // The server has declared this tab too old to trust. Not advisory.
    if (response.status === CONTRACT_REJECTED_STATUS
      && response.headers.get(CONTRACT_REJECTION_HEADER) !== null) {
      useBuildStore().requireRefresh('server-rejected');
    }
  };

  const getHeaders = () => ({
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    // Even though fetch doesn't actually use XMLHttpRequest, we need to set this header because
    // Laravel uses it to determine whether to respond to some requests with JSON or a view/redirect
    'X-Requested-With': 'XMLHttpRequest',
    'X-XSRF-TOKEN': getToken(),
    // Lets the server refuse this request if the deploy has declared tabs of
    // this vintage incompatible. See constants/ClientContract.
    ...contractHeader(),
  });

  const get = async <T>(url: string, priority: RequestPriority = 'auto'): Promise<T> => {
    controller.value = new AbortController();
    const { signal } = controller.value;

    try {
      // Without these headers Laravel treats a GET as a browser navigation and
      // answers an expired session with a 302 to /login, recording this URL as
      // `url.intended` — which is how users ended up redirected to ajax
      // endpoints after logging back in.
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: getHeaders(),
        priority,
        signal,
      });

      noteAuthFailure(response);

      // Callers treat a null return as "no data". Keep that contract rather than
      // handing back the error body now that failures arrive as JSON.
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data as T;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      controller.value = undefined;
    }
  };

  const postOrPut = async <T, U extends any | Record<string, string>>(url: string, payload: U, type: 'POST' | 'PUT', formEncoded: boolean, priority: RequestPriority): Promise<T> => {
    const headers = getHeaders();

    controller.value = new AbortController();
    const { signal } = controller.value;

    let body: string;
    if (formEncoded) {
      body = new URLSearchParams(payload as Record<string, string>).toString();
    } else {
      body = JSON.stringify(payload);
    }

    if (formEncoded) {
      headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
    }

    try {
      const response = await fetch(url, {
        method: type,
        credentials: 'same-origin',
        headers: headers,
        body: body,
        priority: priority,
        signal,
      });

      noteAuthFailure(response);

      if (response?.headers.has('content-type')) {
        if (response.headers.get('content-type').includes('text/html')) {
          return await response.text() as T;
        }
      }

      const data = await response.json();
      return data as T;
    } catch {
      return undefined;
    } finally {
      controller.value = undefined;
    }
  };

  const destroy = async <T>(url: string, payload?: any): Promise<T> => {
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getToken(),
      ...contractHeader(),
    };

    controller.value = new AbortController();
    const { signal } = controller.value;

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: headers,
        body: payload ? JSON.stringify(payload) : '',
        signal,
      });

      noteAuthFailure(response);

      const data = await response.json();

      return data as T;
    } catch {
      return undefined;
    } finally {
      controller.value = undefined;
    }
  };

  /**
   * POST JSON and hand back the status alongside the body, for callers that have
   * to tell an expected failure from an unexpected one. `extraHeaders` exists so
   * a caller holding a freshly minted CSRF token can send it explicitly rather
   * than relying on the cookie having been updated in time.
   */
  const postForResult = async <T>(url: string, payload: unknown, extraHeaders: Record<string, string> = {}): Promise<FetchResult<T>> => {
    controller.value = new AbortController();
    const { signal } = controller.value;

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { ...getHeaders(), ...extraHeaders },
        body: JSON.stringify(payload),
        signal,
      });

      noteAuthFailure(response);

      let data: T | null = null;
      try {
        data = await response.json() as T;
      } catch {
        // Not JSON — an HTML error page or an empty body. The status still
        // carries the meaning, so report it rather than failing outright.
      }

      return { status: response.status, ok: response.ok, data };
    } catch {
      // Network-level failure: no status to report.
      return { status: 0, ok: false, data: null };
    } finally {
      controller.value = undefined;
    }
  };

  const put = async<T, U extends any | Record<string, string> = object>(url: string, payload: U, formEncoded = false, priority: RequestPriority = 'auto'): Promise<T> => postOrPut<T, U>(url, payload, 'PUT', formEncoded, priority);

  const post = async<T, U extends any | Record<string, string> = object>(url: string, payload: U, formEncoded = false, priority: RequestPriority = 'auto'): Promise<T> => postOrPut<T, U>(url, payload, 'POST', formEncoded, priority);

  const postFormData = async <T>(url: string, payload: FormData): Promise<T> => {
    const headers = {
      'Accept': 'application/json, text/plain, */*',
      'Cache-Control': 'no-cache',
      'Sec-Fetch-Mode': 'cors',
      'Sec-Fetch-Site': 'same-origin',
      'X-Requested-With': 'XMLHttpRequest',
      'X-XSRF-TOKEN': getToken(),
      ...contractHeader(),
    };

    controller.value = new AbortController();
    const { signal } = controller.value;

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers,
        body: payload,
        signal,
      });

      noteAuthFailure(response);

      const data = await response.json();

      return data as T;
    } catch {
      return undefined;
    } finally {
      controller.value = undefined;
    }
  };

  return {
    controller,
    destroy,
    get,
    post,
    postForResult,
    postFormData,
    put,
  };
};
